/**
 * Text transformation types for different transformers
 */
export enum TransformationType {
	LINE_BREAKS = "lineBreaks",
	YOUTUBE_LINKS = "youtubeLinks",
	URL_TO_LINKS = "urlToLinks",
	EMAIL_TO_LINKS = "emailToLinks",
}

/**
 * Configuration for text transformations
 */
export interface TransformationConfig {
	type: TransformationType;
	options?: Record<string, unknown>;
}

/**
 * TextTransformService - A parameterizable service for text transformations
 */
export class TextTransformService {
	private transformers: Map<TransformationType, (text: string, options?: Record<string, unknown>) => string> =
		new Map();

	constructor() {
		this.initializeTransformers();
	}

	/**
	 * Initialize all available transformers
	 */
	private initializeTransformers(): void {
		this.transformers.set(TransformationType.LINE_BREAKS, this.transformLineBreaks);
		this.transformers.set(TransformationType.YOUTUBE_LINKS, this.transformYouTubeLinks);
		this.transformers.set(TransformationType.URL_TO_LINKS, this.transformUrlToLinks);
		this.transformers.set(TransformationType.EMAIL_TO_LINKS, this.transformEmailToLinks);
	}

	/**
	 * Transform line breaks to HTML br tags
	 */
	private transformLineBreaks(text: string): string {
		return text.replace(/\r\n/g, "<br>").replace(/\n/g, "<br>");
	}

	/**
	 * Transform YouTube URLs in brackets to embedded thumbnails with links
	 */
	private transformYouTubeLinks(text: string, options?: Record<string, unknown>): string {
		const quality = typeof options?.thumbnailQuality === "string" ? options.thumbnailQuality : "0";
		const youtubeRegex = /\[(https?:\/\/(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]{11}))]/g;

		return text.replace(youtubeRegex, (match, fullUrl, videoId) => {
			const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/${quality}.jpg`;

			return `
        <a href="${fullUrl}" target="_blank" 
        style="aspect-ratio: 4/3;
            display: block;
            margin-bottom: 2.5rem;
            position: relative;
        ">
            <img src="${thumbnailUrl}" alt="YouTube video thumbnail" class="max-w-full" 
            style="
                position: absolute;
                left: 0;
                right: 0;
            "/>
        </a>
      `;
		});
	}

	/**
	 * Transform plain URLs to HTML links with target="_blank"
	 */
	private transformUrlToLinks(text: string, options?: { className?: string; title?: string }): string {
		const urlRegex = /(?<!['"'])(https?:\/\/[^\s<>"']+)(?!['"'])/g;
		const className = options?.className || "";
		const title = options?.title || "";

		return text.replace(urlRegex, (url) => {
			const classAttr = className ? ` class="${className}"` : "";
			const titleAttr = title ? ` title="${title}"` : "";
			const displayText = url.replace(/^https?:\/\//, "");

			return `<a href="${url}" target="_blank"${classAttr}${titleAttr}>${displayText}</a>`;
		});
	}

	/**
	 * Transform email addresses to mailto links
	 */
	private transformEmailToLinks(text: string, options?: { className?: string; subject?: string }): string {
		const emailRegex = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;
		const className = options?.className || "";
		const subject = options?.subject || "";

		return text.replace(emailRegex, (email) => {
			const classAttr = className ? ` class="${className}"` : "";
			const href = subject ? `mailto:${email}?subject=${encodeURIComponent(subject)}` : `mailto:${email}`;

			return `<a href="${href}"${classAttr}>${email}</a>`;
		});
	}

	/**
	 * Apply transformations to text based on configuration
	 */
	public transform(text: string, configs: TransformationConfig[]): string {
		if (!text?.length) {
			return "";
		}

		let result = text;

		// Apply transformations in the order they are provided
		for (const config of configs) {
			const transformer = this.transformers.get(config.type);
			if (transformer) {
				result = transformer(result, config.options);
			}
		}

		return result;
	}
}

// Export a singleton instance
export const textTransformService = new TextTransformService();
